/**
 * HotkeyCapture - Interactive widget for recording keyboard shortcut bindings
 *
 * States:
 * - Display: shows current binding as <kbd> elements
 * - Listening: pulsing border, captures next key combo or starts hold recording
 *
 * Supports two binding types:
 * - Combo: modifier + key pressed simultaneously
 * - Hold: key pressed and held for a duration
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import type { HotkeyBinding } from '@threadheads/shared';

interface HotkeyCaptureProps {
  /** Current binding (null = unbound) */
  value: HotkeyBinding | null;
  /** Called when binding changes */
  onChange: (binding: HotkeyBinding | null) => void;
  /** Action label for accessibility */
  actionLabel: string;
  /** Whether the value has been modified from saved state */
  isDirty?: boolean;
  /** Whether editing is disabled (read-only for staff users) */
  disabled?: boolean;
  /** When true, only combo bindings are allowed (no hold mode toggle) */
  comboOnly?: boolean;
}

/** Format a binding for display */
export function formatBinding(binding: HotkeyBinding | null): { keys: string[]; suffix?: string } {
  if (!binding) return { keys: ['Unbound'] };

  if (binding.type === 'combo') {
    const parts: string[] = [];
    if (binding.modifiers.includes('ctrl')) parts.push('Ctrl');
    if (binding.modifiers.includes('alt')) parts.push('Alt');
    if (binding.modifiers.includes('meta')) parts.push('Cmd');
    if (binding.modifiers.includes('shift')) parts.push('Shift');
    // Format key name for display
    let keyName = binding.key;
    if (keyName === 'Enter') keyName = '↵';
    else if (keyName === 'Backspace') keyName = '⌫';
    else if (keyName === ' ') keyName = 'Space';
    else if (keyName.length === 1) keyName = keyName.toUpperCase();
    parts.push(keyName);
    return { keys: parts };
  }

  // Hold binding
  let keyName = binding.key;
  if (keyName === 'Enter') keyName = '↵';
  else if (keyName === 'Backspace') keyName = '⌫';
  else if (keyName.length === 1) keyName = keyName.toUpperCase();

  return {
    keys: [`Hold ${keyName}`],
    suffix: `${binding.durationMs}ms`,
  };
}

type BindingMode = 'combo' | 'hold';
type HoldPreset = 300 | 500 | 1000;

export function HotkeyCapture({
  value,
  onChange,
  actionLabel,
  isDirty = false,
  disabled = false,
  comboOnly = false,
}: HotkeyCaptureProps) {
  const [isListening, setIsListening] = useState(false);
  const [bindingMode, setBindingMode] = useState<BindingMode>(comboOnly ? 'combo' : (value?.type ?? 'combo'));
  const [holdKey, setHoldKey] = useState<string | null>(null);
  const [holdDuration, setHoldDuration] = useState<HoldPreset>(500);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Start listening for key input
  const startListening = useCallback(() => {
    if (disabled) return;
    setIsListening(true);
    setError(null);
    setHoldKey(null);
    // Auto-timeout after 5 seconds
    timeoutRef.current = setTimeout(() => {
      setIsListening(false);
      setError(null);
    }, 5000);
  }, [disabled]);

  // Cancel listening
  const cancelListening = useCallback(() => {
    setIsListening(false);
    setError(null);
    setHoldKey(null);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  // Clear binding
  const handleClear = useCallback(() => {
    onChange(null);
  }, [onChange]);

  // Confirm hold binding
  const confirmHold = useCallback(() => {
    if (!holdKey) return;
    onChange({
      type: 'hold',
      key: holdKey,
      durationMs: holdDuration,
    });
    setIsListening(false);
    setHoldKey(null);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, [holdKey, holdDuration, onChange]);

  // Listen for key events when in listening mode
  useEffect(() => {
    if (!isListening) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Escape cancels
      if (e.key === 'Escape') {
        cancelListening();
        return;
      }

      // Skip pure modifier presses (wait for the actual key)
      if (['Control', 'Alt', 'Meta', 'Shift'].includes(e.key)) return;

      if (bindingMode === 'combo') {
        // Combo mode: require at least one modifier
        const modifiers: Array<'ctrl' | 'alt' | 'meta' | 'shift'> = [];
        if (e.ctrlKey) modifiers.push('ctrl');
        if (e.altKey) modifiers.push('alt');
        if (e.metaKey) modifiers.push('meta');
        if (e.shiftKey) modifiers.push('shift');

        const hasRequiredModifier = modifiers.some((m) => m === 'ctrl' || m === 'alt' || m === 'meta');
        if (!hasRequiredModifier) {
          setError('Must include Ctrl, Alt, or Cmd');
          return;
        }

        // Capture the combo
        onChange({
          type: 'combo',
          modifiers,
          key: e.key,
        });
        setIsListening(false);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      } else {
        // Hold mode: capture the key, then user picks duration
        setHoldKey(e.key);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isListening, bindingMode, onChange, cancelListening]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const { keys, suffix } = formatBinding(value);

  // Listening mode - hold key captured, show duration selection
  if (isListening && holdKey) {
    let displayKey = holdKey;
    if (displayKey === 'Enter') displayKey = '↵';
    else if (displayKey === 'Backspace') displayKey = '⌫';
    else if (displayKey.length === 1) displayKey = displayKey.toUpperCase();

    return (
      <div className="space-y-3 border-2 border-primary rounded-lg p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Hold</span>
            <kbd className="px-2 py-0.5 text-xs font-mono bg-primary/10 rounded border border-primary shadow-sm">
              {displayKey}
            </kbd>
            <span className="text-sm text-muted-foreground">for:</span>
          </div>
          <Button variant="ghost" size="sm" onClick={cancelListening} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <RadioGroup
          value={String(holdDuration)}
          onValueChange={(v) => setHoldDuration(Number(v) as HoldPreset)}
          className="flex gap-2"
        >
          {([300, 500, 1000] as const).map((ms) => (
            <div key={ms} className="flex items-center">
              <RadioGroupItem value={String(ms)} id={`hold-${ms}`} className="sr-only" />
              <Label
                htmlFor={`hold-${ms}`}
                className={cn(
                  'px-3 py-2 rounded-md border cursor-pointer text-sm transition-colors min-h-[44px] flex items-center',
                  holdDuration === ms
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted/30 hover:bg-muted/50 border-border',
                )}
              >
                {ms === 300 ? 'Fast' : ms === 500 ? 'Normal' : 'Slow'} ({ms}ms)
              </Label>
            </div>
          ))}
        </RadioGroup>
        <Button onClick={confirmHold} size="sm" className="w-full h-11">
          Confirm
        </Button>
      </div>
    );
  }

  // Listening mode - waiting for key press
  if (isListening) {
    return (
      <div className="space-y-2">
        <div
          className="border-2 border-primary rounded-lg p-3 animate-pulse flex items-center justify-between min-h-[44px]"
          role="status"
          aria-live="polite"
        >
          <span className="text-sm text-muted-foreground">
            {bindingMode === 'combo' ? 'Press modifier + key...' : 'Press a key to hold...'}
          </span>
          <Button variant="ghost" size="sm" onClick={cancelListening} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
        {/* Mode toggle (hidden when comboOnly) */}
        {!comboOnly && <RadioGroup
          value={bindingMode}
          onValueChange={(v) => {
            setBindingMode(v as BindingMode);
            setError(null);
          }}
          className="flex gap-2"
        >
          <div className="flex items-center">
            <RadioGroupItem value="combo" id="mode-combo" className="sr-only" />
            <Label
              htmlFor="mode-combo"
              className={cn(
                'px-3 py-2.5 rounded-md border cursor-pointer text-xs transition-colors min-h-[44px] flex items-center',
                bindingMode === 'combo'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted/30 hover:bg-muted/50 border-border',
              )}
            >
              Combo
            </Label>
          </div>
          <div className="flex items-center">
            <RadioGroupItem value="hold" id="mode-hold" className="sr-only" />
            <Label
              htmlFor="mode-hold"
              className={cn(
                'px-3 py-2.5 rounded-md border cursor-pointer text-xs transition-colors min-h-[44px] flex items-center',
                bindingMode === 'hold'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted/30 hover:bg-muted/50 border-border',
              )}
            >
              Hold
            </Label>
          </div>
        </RadioGroup>}
        {error && (
          <p className="text-xs text-destructive">{error}</p>
        )}
      </div>
    );
  }

  // Display mode
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={startListening}
        disabled={disabled}
        className={cn(
          'flex items-center gap-1 px-2 py-1.5 rounded-md border transition-colors min-h-[44px]',
          disabled
            ? 'cursor-not-allowed opacity-50'
            : 'cursor-pointer hover:bg-muted/50',
          isDirty && 'border-amber-400 dark:border-amber-600',
        )}
        aria-label={`Change hotkey for ${actionLabel}. Current: ${keys.join(' + ')}${suffix ? ` (${suffix})` : ''}`}
      >
        {keys.map((key, i) => (
          <kbd
            key={i}
            className={cn(
              'px-2 py-0.5 text-xs font-mono rounded border shadow-sm',
              value === null
                ? 'bg-muted/50 text-muted-foreground border-border/50'
                : isDirty
                  ? 'bg-amber-100 dark:bg-amber-950 border-amber-300 dark:border-amber-700'
                  : 'bg-muted border-border',
            )}
          >
            {key}
          </kbd>
        ))}
        {suffix && (
          <span className="text-xs text-muted-foreground ml-1">{suffix}</span>
        )}
      </button>
      {value && !disabled && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
          aria-label={`Clear hotkey for ${actionLabel}`}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}
